import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPhoneAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Factory Tour', path: '/manufacturing-process' },
    { name: 'Our Products', path: '/our-product-page' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* Navbar */}
      <motion.div
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? 'backdrop-blur-xl bg-white/70 shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-[80px]">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <motion.img
                whileHover={{ scale: 1.05 }}
                src="/logo/logo.png"
                alt="logo"
                className="w-[55px] hidden md:block"
              />
              <h1 className="text-xl md:text-2xl font-bold tracking-wide">
                <span className="text-black">IES</span>{' '}
                <span className="text-red-600 group-hover:text-black transition">
                  Gear
                </span>
              </h1>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-10 text-[15px] font-medium">
              {menuItems.map((item, i) => (
                <motion.div key={i} whileHover={{ y: -2 }} className="relative group">
                  <Link to={item.path} className="text-black tracking-wide">
                    {item.name}
                  </Link>
                  <span className="absolute left-0 -bottom-1 w-0 h-[2px] bg-red-600 transition-all duration-300 group-hover:w-full"></span>
                </motion.div>
              ))}
            </div>

            {/* Right */}
            <div className="flex items-center gap-4">
              <motion.a
                whileHover={{ scale: 1.05 }}
                href="tel:+971557711247"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full border border-black text-black hover:bg-black hover:text-white transition"
              >
                <FaPhoneAlt /> Call
              </motion.a>

              <motion.div whileHover={{ scale: 1.05 }}>
                <Link to="/contact" className="px-5 py-2 rounded-full bg-black text-white hover:bg-red-600 transition">
                  Send Inquiry
                </Link>
              </motion.div>

              {/* Premium Animated Hamburger */}
              <button onClick={() => setMenuOpen(true)} className="lg:hidden">
                <div className="space-y-1">
                  <motion.span animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 6 : 0 }} className="block w-6 h-[2px] bg-black" />
                  <motion.span animate={{ opacity: menuOpen ? 0 : 1 }} className="block w-6 h-[2px] bg-black" />
                  <motion.span animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -6 : 0 }} className="block w-6 h-[2px] bg-black" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Custom Luxury Drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />

            {/* Drawer Panel */}
            <motion.div
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 left-0 w-80 h-full bg-white z-50 p-8 shadow-2xl"
            >
              <h2 className="text-2xl font-bold mb-10">Menu</h2>

              <ul className="space-y-6">
                {menuItems.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      className="text-lg text-black hover:text-red-600 transition"
                    >
                      {item.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;